import { useRef, useEffect, useLayoutEffect, useCallback } from 'react'

export interface UseSwipeTabsOptions {
    /** The ordered list of tabs currently visible */
    tabs: { id: string; label: string }[]
    /** The currently active tab id */
    activeId: string
    /** Called with the id of the tab to switch to */
    onChange: (id: string) => void
    /**
     * Fraction of container width (0–1) required to trigger a tab change.
     * A fast flick (velocity > 0.3 px/ms) can also trigger with less distance.
     * Default: 0.3
     */
    threshold?: number
}

export interface UseSwipeTabsReturn {
    /** Attach to the outer overflow:hidden wrapper div */
    containerRef: React.RefObject<HTMLDivElement | null>
    /** Attach to the inner flex track div that holds all panels side-by-side */
    trackRef: React.RefObject<HTMLDivElement | null>
}

/**
 * Detects horizontal swipe gestures and animates between tabs.
 *
 * - During drag: the track follows the finger directly (no CSS transition).
 * - On release past threshold (or fast flick): snaps to next/previous tab with
 *   a CSS ease-out transition, then fires onChange.
 * - On release below threshold: snaps back to the current tab.
 * - Vertical swipes are ignored so normal page scrolling still works.
 * - When the active tab changes from outside (e.g. tab bar click), the track
 *   jumps immediately to the new position (no transition).
 */
export function useSwipeTabs({
    tabs,
    activeId,
    onChange,
    threshold = 0.3,
}: UseSwipeTabsOptions): UseSwipeTabsReturn {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const trackRef = useRef<HTMLDivElement | null>(null)

    // Mirror mutable props into refs so event handlers stay stable.
    const tabsRef = useRef(tabs)
    const activeIdRef = useRef(activeId)
    const activeIndexRef = useRef(tabs.findIndex(t => t.id === activeId))
    const onChangeRef = useRef(onChange)
    const thresholdRef = useRef(threshold)
    useLayoutEffect(() => {
        tabsRef.current = tabs
        activeIdRef.current = activeId
        activeIndexRef.current = tabs.findIndex(t => t.id === activeId)
        onChangeRef.current = onChange
        thresholdRef.current = threshold
    })

    // When a swipe fires onChange, we set this flag so the layout effect below
    // doesn't override the transform we just animated to.
    const pendingSwipeRef = useRef(false)

    // Sync the track transform whenever the active tab changes from outside
    // (e.g. user taps the tab bar).  Skip when the change came from a swipe.
    useLayoutEffect(() => {
        const track = trackRef.current
        if (!track) return
        if (pendingSwipeRef.current) {
            pendingSwipeRef.current = false
            return
        }
        const idx = tabs.findIndex(t => t.id === activeId)
        if (idx === -1) return
        track.style.transition = 'none'
        track.style.transform = `translateX(-${idx * 100}%)`
    }, [activeId, tabs])

    // Touch tracking — all refs so there are no re-renders during gesture
    const startX = useRef(0)
    const startY = useRef(0)
    const currentX = useRef(0)
    const startTime = useRef(0)
    const gestureDecided = useRef<'horizontal' | 'vertical' | null>(null)
    const isAnimating = useRef(false)

    /** Directly move the track without a transition (follows finger). */
    const moveTrack = useCallback((offsetPx: number) => {
        const track = trackRef.current
        if (!track) return
        const base = -activeIndexRef.current * 100
        track.style.transition = 'none'
        track.style.transform = `translateX(calc(${base}% + ${offsetPx}px))`
    }, [])

    /** Animate the track to a target tab index, then call onComplete. */
    const snapTo = useCallback((targetIndex: number, onComplete?: () => void) => {
        const track = trackRef.current
        if (!track) return
        isAnimating.current = true
        track.style.transition = 'transform 0.25s ease-out'
        track.style.transform = `translateX(-${targetIndex * 100}%)`

        const cleanup = () => {
            isAnimating.current = false
            track.removeEventListener('transitionend', cleanup)
            onComplete?.()
        }
        track.addEventListener('transitionend', cleanup)
        // Safety fallback if transitionend doesn't fire (e.g. hidden tab)
        setTimeout(cleanup, 300)
    }, [])

    const handleTouchStart = useCallback((e: TouchEvent) => {
        if (isAnimating.current) return
        startX.current = e.touches[0].clientX
        startY.current = e.touches[0].clientY
        currentX.current = e.touches[0].clientX
        startTime.current = Date.now()
        gestureDecided.current = null
    }, [])

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            if (isAnimating.current) return
            if (gestureDecided.current === 'vertical') return

            const x = e.touches[0].clientX
            const y = e.touches[0].clientY
            currentX.current = x

            const dx = x - startX.current
            const dy = y - startY.current

            if (gestureDecided.current === null) {
                if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
                gestureDecided.current = Math.abs(dx) >= Math.abs(dy) ? 'horizontal' : 'vertical'
            }

            if (gestureDecided.current === 'horizontal') {
                e.preventDefault()

                const idx = activeIndexRef.current
                const currentTabs = tabsRef.current
                // Apply rubber-banding at the edges
                let clampedDx = dx
                if (dx > 0 && idx === 0) clampedDx = dx * 0.2
                if (dx < 0 && idx === currentTabs.length - 1) clampedDx = dx * 0.2

                moveTrack(clampedDx)
            }
        },
        [moveTrack]
    )

    const handleTouchEnd = useCallback(() => {
        if (gestureDecided.current !== 'horizontal') {
            gestureDecided.current = null
            return
        }

        gestureDecided.current = null

        const dx = currentX.current - startX.current
        const dt = Math.max(Date.now() - startTime.current, 1)
        const velocity = Math.abs(dx) / dt // px/ms

        const containerWidth = containerRef.current?.offsetWidth ?? window.innerWidth
        const thresholdPx = containerWidth * thresholdRef.current
        const isDistanceSwipe = Math.abs(dx) >= thresholdPx
        const isVelocitySwipe = velocity > 0.3 && Math.abs(dx) > 20

        const currentTabs = tabsRef.current
        const idx = activeIndexRef.current

        if ((isDistanceSwipe || isVelocitySwipe) && dx < 0 && idx < currentTabs.length - 1) {
            // Swipe left → next tab
            const nextId = currentTabs[idx + 1].id
            pendingSwipeRef.current = true
            snapTo(idx + 1)
            onChangeRef.current(nextId)
        } else if ((isDistanceSwipe || isVelocitySwipe) && dx > 0 && idx > 0) {
            // Swipe right → previous tab
            const prevId = currentTabs[idx - 1].id
            pendingSwipeRef.current = true
            snapTo(idx - 1)
            onChangeRef.current(prevId)
        } else {
            // Not far/fast enough — snap back
            snapTo(idx)
        }
    }, [snapTo])

    const handleTouchCancel = useCallback(() => {
        if (gestureDecided.current === 'horizontal') {
            // Reset to current tab position immediately (no animation, no blocking)
            const track = trackRef.current
            if (track) {
                track.style.transition = 'none'
                track.style.transform = `translateX(-${activeIndexRef.current * 100}%)`
            }
        }
        gestureDecided.current = null
    }, [])

    useEffect(() => {
        const el = containerRef.current
        if (!el || tabs.length <= 1) return

        el.addEventListener('touchstart', handleTouchStart, { passive: true })
        el.addEventListener('touchmove', handleTouchMove, { passive: false })
        el.addEventListener('touchend', handleTouchEnd)
        el.addEventListener('touchcancel', handleTouchCancel)

        return () => {
            el.removeEventListener('touchstart', handleTouchStart)
            el.removeEventListener('touchmove', handleTouchMove)
            el.removeEventListener('touchend', handleTouchEnd)
            el.removeEventListener('touchcancel', handleTouchCancel)
        }
    }, [tabs.length, handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel])

    return { containerRef, trackRef }
}
